import { RING_MASS_DRAG, RING_ORBIT_RATE, TAU } from '../constants';
import { norm } from '../polar';
import { ringMass, ringSpeedFor } from '../world';
import type { Content, Input, World } from '../types';

/**
 * The ring turns on its own. Nobody steers it.
 *
 * That splits the two layers cleanly: the crust holds a bearing, the ring
 * sweeps every bearing in turn. Coverage stops being a move you make and
 * becomes a rhythm you build around — where your ring guns *will* be in three
 * seconds is the thing to plan against.
 *
 * Mass still matters, just differently: a ring loaded with moons orbits slower
 * and therefore covers less ground per wave. That's not real orbital mechanics
 * — period doesn't depend on mass — but without it moons would be free, and it
 * preserves what §4.3 was for.
 *
 * The orbit is parked during the build phase. Fourteen seconds a revolution is
 * the right speed to plan around and the wrong speed to *aim at* — placing a
 * gun on a mount that's sliding out from under your thumb is a dexterity test
 * nobody asked for. Nothing is attacking during prep, so the orbit has no work
 * to do; it spins up when you commit. The crust keeps turning either way: it's
 * an order of magnitude slower, and a board where nothing at all moves looks
 * broken.
 */
export function updateRotation(
  world: World,
  content: Content,
  input: Input,
  dt: number,
): void {
  // Natural crust spin. Surface slots ride it unless individually locked.
  world.planetRot = norm(world.planetRot + world.planet.spin * dt);

  // Machina's Servo Ring and the Servos upgrade both make the orbit quicker,
  // which is straightforwardly more coverage and more juice swept up.
  const speed = ringSpeedFor(world);
  const mass = ringMass(world, content);
  const drag = 1 / (1 + (mass - 1) * RING_MASS_DRAG);
  const slow = world.modifiers.has('slow-orbit') ? 0.4 : 1;
  const parked = world.waveState === 'prep' ? 0 : 1;

  world.ringVel = RING_ORBIT_RATE * speed * drag * slow * parked;
  world.ringRot = norm(world.ringRot + world.ringVel * dt);
  world.stats.ringDistance += Math.abs(world.ringVel * dt);

  void input;
}

export { TAU };
